// Game configuration constants
export const MAX_ATTEMPTS = 6

const STORAGE_KEYS = {
    ATTEMPT_DICT: "attemptDict",
    CURRENT_ATT: "currentAtt",
    CURRENT_INDEX: "currentIndex",
    ANSWER_DICT: "answerDictionary",
    GAME_STATUS: "gameStatus",
    GAME_DATE: "gameDate",
    LAST_COMPLETED: "lastCompleted",
    LAST_TIME_PLAYED: "lastTimePlayed",
    STREAK: "streak",
    MAX_STREAK: "maxStreak",
    GUESS_DIST: "guessDist",
    LAST_WIN_DATE: "lastWinDate",
}

const parseJSON = (key, fallback) => {
    const raw = localStorage.getItem(key)
    if (raw === null) {
        return fallback
    }

    try {
        return JSON.parse(raw)
    } catch (_error) {
        return fallback
    }
}

const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

export const getTodayKey = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = `${now.getMonth() + 1}`.padStart(2, "0")
    const day = `${now.getDate()}`.padStart(2, "0")
    return `${year}-${month}-${day}`
}

export const normalizeToken = (token) => {
    if (token === " ") {
        return token
    }

    return (token || "").normalize("NFC").toUpperCase()
}

export const tokenizeWord = (word) => {
    const normalized = (word || "").normalize("NFC")
    if (typeof Intl !== "undefined" && typeof Intl.Segmenter !== "undefined") {
        const segmenter = new Intl.Segmenter("yo", { granularity: "grapheme" })
        return Array.from(segmenter.segment(normalized), ({ segment }) => segment)
    }

    return Array.from(normalized)
}

// Function to generate attempts dictionary for any word length
export const generateAttempts = (wordLength) => {
    const attempts = {}
    for (let i = 1; i <= MAX_ATTEMPTS; i += 1) {
        attempts[i] = Array(wordLength).fill(" ")
    }
    return attempts
}

// Function to generate answer dictionary
export const generateAnswerDictionary = () => {
    const dict = {}
    for (let i = 1; i <= MAX_ATTEMPTS; i += 1) {
        dict[i] = null
    }
    return dict
}

const buildDefaultState = (wordLength) => ({
    attemptDict: generateAttempts(wordLength),
    currentAtt: 1,
    currentIndex: 0,
    answerDictionary: generateAnswerDictionary(),
    gameStatus: "IN_PROGRESS",
})

const normalizeAttemptDict = (attemptDict, wordLength) => {
    const fallback = generateAttempts(wordLength)
    if (!attemptDict || typeof attemptDict !== "object") {
        return fallback
    }

    const normalized = {}
    for (let i = 1; i <= MAX_ATTEMPTS; i += 1) {
        const row = attemptDict[i]
        if (!Array.isArray(row) || row.length !== wordLength) {
            normalized[i] = fallback[i]
            continue
        }

        normalized[i] = row.map((token) => normalizeToken(token))
    }

    return normalized
}

const normalizeAnswerDictionary = (answerDictionary) => {
    const fallback = generateAnswerDictionary()
    if (!answerDictionary || typeof answerDictionary !== "object") {
        return fallback
    }

    const normalized = {}
    for (let i = 1; i <= MAX_ATTEMPTS; i += 1) {
        const row = answerDictionary[i]
        normalized[i] = Array.isArray(row) ? row : null
    }

    return normalized
}

const persistState = (state) => {
    localStorage.setItem(STORAGE_KEYS.ATTEMPT_DICT, JSON.stringify(state.attemptDict))
    localStorage.setItem(STORAGE_KEYS.CURRENT_ATT, JSON.stringify(state.currentAtt))
    localStorage.setItem(STORAGE_KEYS.CURRENT_INDEX, JSON.stringify(state.currentIndex))
    localStorage.setItem(STORAGE_KEYS.ANSWER_DICT, JSON.stringify(state.answerDictionary))
    localStorage.setItem(STORAGE_KEYS.GAME_STATUS, state.gameStatus)
}

const Helpers = {
    initializeGameState: (wordLength) => {
        const storedDate = localStorage.getItem(STORAGE_KEYS.GAME_DATE)
        const todayKey = getTodayKey()
        if (!storedDate || storedDate !== todayKey) {
            return Helpers.dayReset(wordLength)
        }

        const defaultState = buildDefaultState(wordLength)
        const gameStatus = localStorage.getItem(STORAGE_KEYS.GAME_STATUS)
        const loadedState = {
            attemptDict: normalizeAttemptDict(parseJSON(STORAGE_KEYS.ATTEMPT_DICT, null), wordLength),
            currentAtt: clamp(Number(parseJSON(STORAGE_KEYS.CURRENT_ATT, 1)) || 1, 1, MAX_ATTEMPTS),
            currentIndex: clamp(Number(parseJSON(STORAGE_KEYS.CURRENT_INDEX, 0)) || 0, 0, wordLength - 1),
            answerDictionary: normalizeAnswerDictionary(parseJSON(STORAGE_KEYS.ANSWER_DICT, null)),
            gameStatus: ["IN_PROGRESS", "WIN", "LOSE"].includes(gameStatus) ? gameStatus : defaultState.gameStatus,
        }

        persistState(loadedState)
        return loadedState
    },

    rowComplete: (currentAttKey) => {
        const attemptDict = parseJSON(STORAGE_KEYS.ATTEMPT_DICT, {})
        const currentAtt = attemptDict[currentAttKey]
        if (!Array.isArray(currentAtt) || currentAtt.length === 0) {
            return false
        }

        const lastElement = currentAtt[currentAtt.length - 1]
        return lastElement !== " "
    },

    boardComplete: () => {
        const currentAtt = Number(parseJSON(STORAGE_KEYS.CURRENT_ATT, 1)) || 1
        return currentAtt >= MAX_ATTEMPTS && Helpers.rowComplete(MAX_ATTEMPTS)
    },

    deleteCharacter: (currentAttKey, currentIndex, setCurrentIndex, setAttemptDict) => {
        const answerAttemptDict = parseJSON(STORAGE_KEYS.ATTEMPT_DICT, {})
        const currentRow = answerAttemptDict[currentAttKey]
        if (!Array.isArray(currentRow) || currentRow.length === 0) {
            return
        }

        const hasAnyCharacter = currentRow.some((entry) => entry !== " ")
        if (!hasAnyCharacter) {
            return
        }

        let indexToDelete = currentIndex - 1
        if (Helpers.rowComplete(currentAttKey)) {
            indexToDelete = currentRow.length - 1
        }

        indexToDelete = clamp(indexToDelete, 0, currentRow.length - 1)
        currentRow[indexToDelete] = " "
        answerAttemptDict[currentAttKey] = currentRow
        setCurrentIndex(indexToDelete)
        localStorage.setItem(STORAGE_KEYS.CURRENT_INDEX, JSON.stringify(indexToDelete))
        localStorage.setItem(STORAGE_KEYS.ATTEMPT_DICT, JSON.stringify(answerAttemptDict))
        setAttemptDict(answerAttemptDict)
    },

    answerCheck: (currentAttKey, answerTokens, setAnswerDictionaryHere, setCurrentAttKey, setCurrentIndex) => {
        if (!Helpers.rowComplete(currentAttKey)) {
            return null
        }

        const answerAttemptDict = parseJSON(STORAGE_KEYS.ATTEMPT_DICT, {})
        const answerAttempt = answerAttemptDict[currentAttKey]
        if (!Array.isArray(answerAttempt)) {
            return null
        }

        const normalizedAnswer = answerTokens.map((entry) => normalizeToken(entry))
        const normalizedAttempt = answerAttempt.map((entry) => normalizeToken(entry))
        const answerColorArr = []

        // First pass: mark exact matches and mark consumed letters.
        const answerCopy = normalizedAnswer.slice()
        for (let i = 0; i < normalizedAnswer.length; i += 1) {
            if (normalizedAnswer[i] === normalizedAttempt[i]) {
                answerColorArr[i] = "correct"
                answerCopy[i] = null
            }
        }

        // Second pass: mark present/absent with duplicate-safe handling.
        for (let i = 0; i < normalizedAnswer.length; i += 1) {
            if (answerColorArr[i] === "correct") {
                continue
            }

            const letterIndex = answerCopy.indexOf(normalizedAttempt[i])
            if (letterIndex !== -1) {
                answerColorArr[i] = "present"
                answerCopy[letterIndex] = null
            } else {
                answerColorArr[i] = "absent"
            }
        }

        const answerDictionaryFromLocal = normalizeAnswerDictionary(parseJSON(STORAGE_KEYS.ANSWER_DICT, null))
        answerDictionaryFromLocal[currentAttKey] = answerColorArr
        const nextCurrentAtt = Math.min(currentAttKey + 1, MAX_ATTEMPTS)
        localStorage.setItem(STORAGE_KEYS.ANSWER_DICT, JSON.stringify(answerDictionaryFromLocal))
        localStorage.setItem(STORAGE_KEYS.CURRENT_ATT, JSON.stringify(nextCurrentAtt))
        setAnswerDictionaryHere(answerDictionaryFromLocal)
        setCurrentAttKey(nextCurrentAtt)
        setCurrentIndex(0)
        localStorage.setItem(STORAGE_KEYS.CURRENT_INDEX, JSON.stringify(0))
        localStorage.setItem(STORAGE_KEYS.LAST_TIME_PLAYED, String(Date.now()))
        return answerColorArr
    },

    correctCheck: (status, answerLength) => {
        const toCheck = status.filter((entry) => entry === "correct")
        return toCheck.length === answerLength
    },

    play: () => {
        const gameStatus = localStorage.getItem(STORAGE_KEYS.GAME_STATUS) || "IN_PROGRESS"
        return gameStatus === "IN_PROGRESS" && !Helpers.boardComplete() && !Helpers.nextDay()
    },

    nextDay: () => {
        const storedDate = localStorage.getItem(STORAGE_KEYS.GAME_DATE)
        if (!storedDate) {
            return false
        }

        return storedDate !== getTodayKey()
    },

    dayReset: (wordLength) => {
        const resetState = buildDefaultState(wordLength)
        persistState(resetState)
        localStorage.setItem(STORAGE_KEYS.GAME_DATE, getTodayKey())
        localStorage.setItem(STORAGE_KEYS.LAST_TIME_PLAYED, String(Date.now()))
        localStorage.removeItem(STORAGE_KEYS.LAST_COMPLETED)
        return resetState
    },

    getStats: () => {
        const streak    = Number(parseJSON(STORAGE_KEYS.STREAK, 0)) || 0
        const maxStreak = Number(parseJSON(STORAGE_KEYS.MAX_STREAK, 0)) || 0
        const rawDist   = parseJSON(STORAGE_KEYS.GUESS_DIST, null)
        const guessDist = (rawDist && typeof rawDist === 'object')
            ? rawDist
            : { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 }
        return { streak, maxStreak, guessDist }
    },

    recordResult: (won, attemptCount) => {
        const today = getTodayKey()
        const lastWinDate = localStorage.getItem(STORAGE_KEYS.LAST_WIN_DATE)
        const { streak, maxStreak, guessDist } = Helpers.getStats()

        let newStreak = streak
        if (won) {
            const yesterday = new Date()
            yesterday.setDate(yesterday.getDate() - 1)
            const yKey = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`
            newStreak = (lastWinDate === yKey || lastWinDate === today) ? streak + 1 : 1
            localStorage.setItem(STORAGE_KEYS.LAST_WIN_DATE, today)
            const newDist = { ...guessDist, [attemptCount]: (guessDist[attemptCount] || 0) + 1 }
            localStorage.setItem(STORAGE_KEYS.GUESS_DIST, JSON.stringify(newDist))
        } else {
            newStreak = 0
        }

        localStorage.setItem(STORAGE_KEYS.STREAK, JSON.stringify(newStreak))
        localStorage.setItem(STORAGE_KEYS.MAX_STREAK, JSON.stringify(Math.max(maxStreak, newStreak)))
    },
}

export default Helpers
